import { useState, useRef, useCallback } from "react";
import { ContentRenderer, type Section } from "@/components/content";
import { MiniRichTextEditor } from "@/components/editor/MiniRichTextEditor";
import { SectionAIToolbar } from "./SectionAIToolbar";
import { adminApi } from "../../api/adminApi";
import { ImagePlus, Trash2, Loader2, Upload } from "lucide-react";
import { Button } from "@learn-me/ui";
import toast from "react-hot-toast";

interface EditableSectionContentProps {
  section: Section;
  isEditing: boolean;
  editingValue: string;
  onContentChange: (value: string) => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  chapterId?: string;
  onContentApplied?: () => void;
}

/**
 * Featured Image Manager for section slideshow
 */
function FeaturedImageManager({
  sectionId,
  currentImageUrl,
  onImageChanged,
}: {
  sectionId: string;
  currentImageUrl?: string;
  onImageChanged: () => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Solo se permiten archivos de imagen");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("La imagen no puede superar 5MB");
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
        await adminApi.uploadSectionFeaturedImage(
          sectionId,
          file,
          (progress) => {
            setUploadProgress(progress);
          },
        );
        toast.success("Imagen destacada subida");
        onImageChanged();
      } catch (error) {
        console.error("Error uploading featured image:", error);
        toast.error("Error al subir la imagen");
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [sectionId, onImageChanged],
  );

  const handleDelete = useCallback(async () => {
    if (!window.confirm("¿Eliminar la imagen destacada?")) return;

    setIsDeleting(true);
    try {
      await adminApi.deleteSectionFeaturedImage(sectionId);
      toast.success("Imagen destacada eliminada");
      onImageChanged();
    } catch (error) {
      console.error("Error deleting featured image:", error);
      toast.error("Error al eliminar la imagen");
    } finally {
      setIsDeleting(false);
    }
  }, [sectionId, onImageChanged]);

  if (currentImageUrl) {
    // Show current image with change/delete options
    return (
      <div className="relative group mb-4">
        <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
          <img
            src={currentImageUrl}
            alt="Imagen destacada"
            className="w-full h-auto max-h-[300px] object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1" />
                Cambiar
              </>
            )}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-1" />
                Eliminar
              </>
            )}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    );
  }

  // No image - show upload zone
  return (
    <div className="mb-4">
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary-400 hover:bg-primary-50/50 transition-colors group"
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
            <span className="text-sm text-gray-600">
              Subiendo... {uploadProgress}%
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <ImagePlus className="h-8 w-8 text-gray-400 group-hover:text-primary-500 transition-colors" />
            <span className="text-sm text-gray-600 group-hover:text-primary-600">
              Agregar imagen destacada
            </span>
            <span className="text-xs text-gray-400">
              Esta imagen aparecerá al inicio de la sección (slideshow)
            </span>
          </div>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
}

/**
 * Wrapper component that toggles between view and edit mode.
 * In view mode: renders content with ContentRenderer (same as student view)
 * In edit mode: renders MiniRichTextEditor in inline mode
 *
 * Includes toolbar with AI actions. Audio controls are handled separately
 * in the SectionPlayer via renderAudioControls prop.
 */
export function EditableSectionContent({
  section,
  isEditing,
  editingValue,
  onContentChange,
  onStartEditing,
  onStopEditing,
  chapterId,
  onContentApplied,
}: EditableSectionContentProps) {
  if (isEditing) {
    return (
      <div className="relative ring-2 ring-primary-500 ring-offset-2 rounded-lg p-2 -mx-2">
        <MiniRichTextEditor
          content={editingValue}
          onChange={onContentChange}
          inline
          autoFocus
          onBlur={onStopEditing}
          sectionId={section.id}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Featured Image Manager (Slideshow) */}
      <FeaturedImageManager
        sectionId={section.id}
        currentImageUrl={section.featuredImageUrl}
        onImageChanged={onContentApplied || (() => {})}
      />

      {/* Toolbar for AI actions */}
      {chapterId && (
        <div className="mb-4 p-2 bg-gray-50 border border-gray-200 rounded-lg">
          <SectionAIToolbar
            sectionId={section.id}
            sectionTitle={section.title}
            chapterId={chapterId}
            onContentApplied={onContentApplied}
          />
        </div>
      )}

      {/* Content - clickable to edit */}
      <div
        onClick={onStartEditing}
        className="cursor-pointer hover:bg-gray-50 rounded-lg transition-colors -mx-2 px-2 py-1 group"
      >
        <ContentRenderer html={section.content} />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-gray-400 mt-2">
          Haz clic para editar
        </div>
      </div>
    </div>
  );
}
